import React, { useState } from 'react';

import { Box, Tab, Tabs } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { PhoneOrEmail } from '@vyce/core/src/views/invites/components/PhoneOrEmail';
import { BulkPhoneEntryApi } from '@vyce/core/src/views/invites/types';
import { TabCountLabel } from '@vyce/core/src/components/TabCountLabel';
import { CompanyWorkerItem } from '@vyce/core/src/types';

import { FormExistingMembersList } from './FormExistingMembersList';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabs: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tab: {
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 600,
      minWidth: 'auto',
    },
  })
);

interface Props {
  companyId: string;
  selectedWorkers: CompanyWorkerItem[];
  toggleWorker: (worker: CompanyWorkerItem) => void;
  isPhoneInputDisabled: boolean;
  handleEmailFieldKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handlePhoneFieldKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  addEmailToList: () => void;
  addPhoneToList: () => void;
  deleteContact: (contact: string) => void;
  emails: string[];
  phones: string[];
  setIsPhoneInputDisabled: (isPhoneInputDisabled: boolean) => void;
  bulkPaste?: BulkPhoneEntryApi;
  bulkLimitHint?: string;
  listMaxHeight?: number | string;
}

export const FormSpecificContacts: React.FC<Props> = ({
  companyId,
  selectedWorkers,
  toggleWorker,
  isPhoneInputDisabled,
  handleEmailFieldKeyDown,
  handlePhoneFieldKeyDown,
  addEmailToList,
  addPhoneToList,
  deleteContact,
  emails,
  phones,
  setIsPhoneInputDisabled,
  bulkPaste,
  bulkLimitHint,
  listMaxHeight,
}) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <Tabs
        className={classes.tabs}
        value={tab}
        onChange={(_, value) => setTab(value)}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab
          disableRipple
          className={classes.tab}
          label={<TabCountLabel title="Existing members" count={selectedWorkers.length} />}
        />
        <Tab
          disableRipple
          className={classes.tab}
          label={<TabCountLabel title="New contacts" count={emails.length + phones.length} />}
        />
      </Tabs>

      {/* Both panels stay mounted so search, scroll and inputs survive tab switching */}
      <Box hidden={tab !== 0}>
        <FormExistingMembersList
          companyId={companyId}
          selectedWorkers={selectedWorkers}
          toggleWorker={toggleWorker}
          maxHeight={listMaxHeight}
        />
      </Box>

      <Box hidden={tab !== 1}>
        {/* Added contacts are listed in the "Sending to" summary instead of under the input */}
        <PhoneOrEmail
          hideContactList
          isPhoneInputDisabled={isPhoneInputDisabled}
          handleEmailFieldKeyDown={handleEmailFieldKeyDown}
          handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
          addEmailToList={addEmailToList}
          addPhoneToList={addPhoneToList}
          deleteContact={deleteContact}
          emails={emails}
          phones={phones}
          setIsPhoneInputDisabled={setIsPhoneInputDisabled}
          bulkPaste={bulkPaste}
          bulkLimitHint={bulkLimitHint}
        />
      </Box>
    </Box>
  );
};
